import { useEffect, useRef, useState } from 'react';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { StorageService } from '~/services/StorageService/StorageService';
import { logger } from '~/utils/logger';

type Fn<T> = () => Promise<T>;

interface Options {
  skip?: boolean;
  onError?: (error: unknown) => void;
}

export const useStaleWhileRevalidate = <T>(
  key: string,
  fetchData: Fn<T>,
  { skip, onError }: Options = {},
) => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // need this to be a ref since we cannot update the ref inside of proimse.then
  const isCachedValueRef = useRef(false);
  const useCachedValue = useFlag(FlagType.UseLocalStorageCache);

  useEffect(() => {
    isCachedValueRef.current = false;
    setData(null);
  }, [key]);

  useEffect(() => {
    if (skip || !useCachedValue) return;

    StorageService.isReady().then(async () => {
      try {
        const stored = await StorageService.getSecureItem<T>(key);
        if (!isCachedValueRef.current && stored !== null) {
          setData(stored);
          isCachedValueRef.current = true;
        }
      } catch (error) {
        logger.hooks('Error loading from storage for key %s: %o', key, error);
      }
    });
  }, [key, useCachedValue]);

  useEffect(() => {
    if (skip) return;

    setIsLoading(true);

    (async () => {
      try {
        const freshData = await fetchData();
        isCachedValueRef.current = true;
        setData(freshData);

        if (useCachedValue) {
          await StorageService.setSecureItem(key, freshData);
        }
      } catch (error) {
        logger.hooks('Error fetching fresh data for key %s: %o', key, error);
        onError?.(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [key, skip]);

  return { data, isLoading, isCachedValue: isCachedValueRef.current };
};
